<template>
  <div>
    <div class="popup-info__area section" v-if="action.description">
      <div class="label">
        {{ $t("labels.description") }}
      </div>
      <formatted-text :value="action.description" />
    </div>

    <div class="popup-info__area section" v-if="costsSize > 0">
      <div class="label">
        {{ $t("labels.costs") }}
      </div>
      <button-list
        :items="costs"
        item-key="elementUid"
        line-class="line_effect"
      >
        <template #default="{ item: cost }">
          {{ elements.byUid[cost.elementUid]?.title }}
        </template>
        <template #after="{ item: cost }">
          <span class="ta-r">{{ cost.value }}</span>
        </template>
      </button-list>
    </div>

    <div class="popup-info__area section" v-if="visibleEffects.length > 0">
      <div class="label">
        {{ $t("objectNames.effect", 3) }}
      </div>

      <div class="line_effect">
        <template v-for="effect in visibleEffects" :key="effect.id">
          <store-item
            :id="effect.affectedId"
            v-slot="{ item: effectItem }"
            :type="effect.affectedType"
          >
            <button-item>
              {{ effectItem.title }}
            </button-item>
          </store-item>
          <effect-amount
            :effect="effect"
            :rounds="rounds"
            :round-index="round?.index"
            :inline-expression="true"
            :index="0"
            :targets="targets"
            :resources="resources"
          />
          <i class="whitespace-nowrap">
            <template v-if="effect.pendingEffectsVisible && getEffectLength(effect) > 1">
              {{
                $t(
                  "labels.effectInXRounds",
                  {
                    effect: effect.visibility == 'visible' ? getEffectSum(effect) : '',
                    roundName: $t(
                      `roundUnits.${simulation?.roundUnit}`,
                      getEffectLength(effect)
                    )
                  },
                  getEffectLength(effect)
                )
              }}
            </template>
          </i>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { mapValues } from "lodash-es";

import FormattedText from "@core/ui/FormattedText.vue";
import ButtonList from "@core/ui/ButtonList.vue";
import EffectAmount from "@editor/items/effect/EffectAmount.vue";
import StoreItem from "@editor/items/StoreItem.vue";
import ButtonItem from "@core/input/ButtonItem.vue";

import { useEffectStore } from "@editor/items/effect/effects.store";
import { useElements } from "@editor/items/element/elements";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";

import Effect from "@editor/items/effect/Effect.js";
import type { ActionItem } from "./ActionItem";
import type { BaseEffectItem } from "../effect/EffectItem";
import type { SimulationRoundItem } from "../simulationRound/SimulationRoundItem";

const props = defineProps<{
  item: ActionItem;
  round?: Partial<SimulationRoundItem>;
}>();

const simulationView = useSimulationViewStore();
const { n, t } = useI18n();

const targets = computed(() => simulationView.targets);
const resources = computed(() => simulationView.resources);
const simulation = computed(() => simulationView.simulation);
const scenario = computed(() => simulationView.scenario);
const rounds = computed(() => simulationView.rounds);

const elements = useElements();

const action = computed(() => {
  return props.item;
});

const visibleEffects = computed(() => {
  if (!action.value) return [];
  const effects = useEffectStore().getByActivator(action.value) ?? [];
  if (scenario.value) {
    if (scenario.value.effectsVisibility == "hidden") return [];
    if (scenario.value.effectsVisibility == "visible") return effects.map((effect): BaseEffectItem => ({ ...effect, visibility: "visible" }));
    if (scenario.value.effectsVisibility == "values-hidden") return effects.map((effect): BaseEffectItem => ({...effect, visibility: "hint" }));
  }
  if (action.value?.effectsVisibility == "hidden") return [];
  if (action.value?.effectsVisibility == "visible") return effects.map((effect): BaseEffectItem => ({ ...effect, visibility: "visible" }));
  if (action.value?.effectsVisibility == "values-hidden") return effects.map((effect): BaseEffectItem => ({...effect, visibility: "hint" }));
  return effects.filter((effect) => effect.visibility !== "hidden");
});

const costs = computed(() => {
  const activatedActions = props.round?.activatedActions ?? [];
  return mapValues(activatedActions.find((a) => a.actionId === action.value.id)?.costs ?? {}, (value, elementUid) => ({
    value,
    elementUid
  }));
});
const costsSize = computed(() => {
  return Object.keys(costs.value).length;
});

function getEffectLength(effect: BaseEffectItem) {
  return effect?.impulseExpressions?.length ?? 0;
}
function getEffectSum(effect: BaseEffectItem) {
  if (Effect.isSpecial(effect)) {
    return t("labels.effect");
  }

  return n(
    Effect.sumExpressions(effect.impulseExpressions),
    "displaySign"
  );
}
</script>
